import {
  Autocomplete,
  createFilterOptions,
  Grid,
  MaterialIcons,
  MaterialUICore,
  TextField,
} from '@iclinic/design-system';
import { useFormikContext } from 'formik';
import React, { forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import DatePickerSADT from 'features/tissInvoicing/components/sadt/DatePickerSADT';
import SelectFieldSADT from 'features/tissInvoicing/components/sadt/SelectFieldSADT';
import TextFieldSADT from 'features/tissInvoicing/components/sadt/TextFieldSADT';
import CollapsibleFormSection from 'features/tissInvoicing/components/ui/CollapsibleFormSection';
import useVersionCompare from 'features/tissInvoicing/hooks/useVersionCompare';
import { DocumentTypeOptions } from 'features/tissInvoicing/state/constants';
import { actions } from 'features/tissInvoicing/state/sadt';
import {
  getAutocomplete,
  getCboOptions,
  getDomainTablesOptions,
  getHiredOptions,
} from 'features/tissInvoicing/state/sadt/selectors';
import {
  ClinicAutocompleteEntry,
  GuideSADTForm,
  HiredSolicitantAutocompleteEntry,
  HiredSolicitantCodeEntry,
  PhysicianAutocompleteEntry,
  ProfessionCodeAutocompleteEntry,
} from 'features/tissInvoicing/types';

const { CardTravel } = MaterialIcons;
const { MenuItem } = MaterialUICore;

const getOptionLabelFn = (
  option:
    | PhysicianAutocompleteEntry
    | ClinicAutocompleteEntry
    | ProfessionCodeAutocompleteEntry,
) => {
  if (typeof option === 'string') return option;

  if (option?.inputValue) return option?.inputValue;

  return option?.name;
};

const renderOptionFn = (
  option:
    | PhysicianAutocompleteEntry
    | ClinicAutocompleteEntry
    | ProfessionCodeAutocompleteEntry,
) => {
  if (option?.inputValue) return `"${option?.inputValue}"`;

  return option?.name;
};

interface SolicitorSectionProps {
  eventId: string;
}

const SolicitorSection = (
  { eventId }: SolicitorSectionProps,
  ref: React.Ref<HTMLButtonElement>,
): JSX.Element => {
  const dispatch = useDispatch();

  const { values, errors, touched, handleBlur, setFieldValue } =
    useFormikContext<GuideSADTForm>();

  const hiredOptions = useSelector(getHiredOptions);
  const { hired_solicitants, physicians } = useSelector(getAutocomplete);

  const { states, councils, serviceCharacters, specialCoverages } = useSelector(
    getDomainTablesOptions,
  );

  const hiredFilter = createFilterOptions<HiredSolicitantAutocompleteEntry>();
  const physicianFilter = createFilterOptions<PhysicianAutocompleteEntry>();
  const cboFilter = createFilterOptions<ProfessionCodeAutocompleteEntry>();

  const cboOptions = useSelector(getCboOptions);

  const { isGreaterThan } = useVersionCompare(values.version_id);
  const versionTissIsGreaterThan3 = isGreaterThan(3);

  const setHiredSolicitants = ({
    code_type,
    code,
  }: HiredSolicitantCodeEntry) => {
    setFieldValue('hired_solicitant_code_type', code_type || '');
    setFieldValue('hired_solicitant_code', code || '');
  };

  const getHiredSolicitantCodes = (
    id: number,
    slug: string,
  ): HiredSolicitantCodeEntry | undefined => {
    if (!hired_solicitants) return undefined;

    return hired_solicitants?.find(
      (solicitant) =>
        String(solicitant.id) === String(id) && solicitant.type === slug,
    );
  };

  const getCBOName = (
    id: number | null,
  ): ProfessionCodeAutocompleteEntry | null =>
    cboOptions.find((cbo) => cbo.id === id) || null;

  const getCBOByCode = (code: string | null) => {
    const cboData = cboOptions.find((cbo) => cbo.code === code);
    return cboData?.id || 0;
  };

  return (
    <CollapsibleFormSection
      sectionTitle="Solicitante e solicitação"
      sectionIcon={<CardTravel />}
      sectionIdentifier="solicitor_data"
      ref={ref}
    >
      <Grid container item spacing={2}>
        <Grid item xs={12} sm={12} md={6} lg={7}>
          <Autocomplete
            id="hired_solicitant_name"
            fullWidth
            freeSolo
            clearOnBlur
            value={values.hired_solicitant_name}
            onBlur={handleBlur}
            options={hiredOptions}
            groupBy={(option) => option.type}
            // @ts-ignore
            onChange={(_, option: HiredSolicitantAutocompleteEntry) => {
              setFieldValue('hired_solicitant_name', option?.name || '');

              if (option?.id && option?.slug) {
                const codeData = getHiredSolicitantCodes(
                  option?.id,
                  option?.slug,
                );

                if (codeData) {
                  setHiredSolicitants(codeData);
                  return;
                }

                dispatch(
                  actions.fetchHiredSolicitantData({
                    eventId,
                    solicitantId: option?.id,
                    type: option?.slug,
                    callback: setHiredSolicitants,
                  }),
                );

                return;
              }

              setFieldValue('hired_solicitant_code_type', '');
              setFieldValue('hired_solicitant_code', '');
            }}
            getOptionLabel={getOptionLabelFn}
            renderOption={renderOptionFn}
            filterOptions={(options, params) => {
              const filtered = hiredFilter(options, params);

              if (params.inputValue !== '') {
                filtered.push({
                  inputValue: params.inputValue,
                  name: params.inputValue,
                  type: 'Não cadastrado',
                });
              }

              return filtered;
            }}
            renderInput={(params) => (
              <TextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...params}
                label="14 - Nome do contratado solicitante"
                name="hired_solicitant_name"
                data-ga="hired_solicitant_name"
                error={
                  !!(
                    errors.hired_solicitant_name &&
                    touched.hired_solicitant_name
                  )
                }
                helperText={
                  touched.hired_solicitant_name && errors.hired_solicitant_name
                }
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={2}>
          <SelectFieldSADT
            label="13 - Tipo"
            identifier="hired_solicitant_code_type"
          >
            {DocumentTypeOptions.map((docTypeOption) => (
              <MenuItem value={docTypeOption.value} key={docTypeOption.value}>
                {docTypeOption.label}
              </MenuItem>
            ))}
          </SelectFieldSADT>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3}>
          <TextFieldSADT
            label="13 - Código na operadora"
            identifier="hired_solicitant_code"
            maxLength={14}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6} lg={6}>
          <Autocomplete
            id="professional_solicitant_name"
            freeSolo
            fullWidth
            options={physicians}
            value={values.professional_solicitant_name}
            clearOnBlur
            onBlur={handleBlur}
            // @ts-ignore
            onChange={(_, option: PhysicianAutocompleteEntry) => {
              setFieldValue(
                'professional_solicitant_name',
                option?.name || option || '',
              );
              setFieldValue(
                'professional_solicitant_council',
                option?.council_name || 0,
              );
              setFieldValue(
                'professional_solicitant_council_number',
                option?.council_number || '',
              );
              setFieldValue(
                'professional_solicitant_council_state',
                option?.council_region || 0,
              );
              setFieldValue(
                'professional_solicitant_cbo',
                getCBOByCode(option?.cbo),
              );
            }}
            getOptionLabel={getOptionLabelFn}
            renderOption={renderOptionFn}
            filterOptions={(options, params) => {
              const filtered = physicianFilter(options, params);

              if (params.inputValue !== '') {
                filtered.push({
                  inputValue: params.inputValue,
                  name: params.inputValue,
                  type: '',
                  council_name: 0,
                  council_number: 0,
                  council_region: 0,
                  cbo: '0',
                });
              }

              return filtered;
            }}
            renderInput={(params) => (
              <TextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...params}
                label="15 - Nome do profissional solicitante"
                name="professional_solicitant_name"
                data-ga="professional_solicitant_name"
                error={
                  !!(
                    errors.professional_solicitant_name &&
                    touched.professional_solicitant_name
                  )
                }
                helperText={
                  touched.professional_solicitant_name &&
                  errors.professional_solicitant_name
                }
              />
            )}
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm={12}
          md={versionTissIsGreaterThan3 ? 6 : 12}
          lg={versionTissIsGreaterThan3 ? 3 : 6}
        >
          <SelectFieldSADT
            label="16 - Conselho profissional"
            identifier="professional_solicitant_council"
          >
            {councils.map((council) => (
              <MenuItem value={council.id} key={council.id}>
                {council.term_short}
              </MenuItem>
            ))}
          </SelectFieldSADT>
        </Grid>

        {versionTissIsGreaterThan3 && (
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <SelectFieldSADT
              label="90 - Cobertura especial"
              identifier="special_coverage"
              value={values.special_coverage}
            >
              {specialCoverages.map((specialCoverage) => (
                <MenuItem
                  value={specialCoverage.id === 0 ? '' : specialCoverage.id}
                  key={specialCoverage.id}
                >
                  {specialCoverage.term}
                </MenuItem>
              ))}
            </SelectFieldSADT>
          </Grid>
        )}

        <Grid item xs={12} sm={12} md={6} lg={4}>
          <TextFieldSADT
            label="17 - Nº no conselho"
            identifier="professional_solicitant_council_number"
            maxLength={15}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3}>
          <SelectFieldSADT
            label="18 - UF"
            identifier="professional_solicitant_council_state"
          >
            {states.map((state) => (
              <MenuItem value={state.id} key={state.id}>
                {state.term}
              </MenuItem>
            ))}
          </SelectFieldSADT>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={5}>
          <Autocomplete
            id="professional_solicitant_cbo"
            fullWidth
            clearOnBlur
            onBlur={handleBlur}
            options={cboOptions}
            noOptionsText="CBO não encontrado"
            value={getCBOName(values.professional_solicitant_cbo)}
            onChange={(_, option) =>
              setFieldValue(
                'professional_solicitant_cbo',
                (option as ProfessionCodeAutocompleteEntry)?.id || '',
              )
            }
            getOptionLabel={getOptionLabelFn}
            renderOption={renderOptionFn}
            filterOptions={(options, params) => cboFilter(options, params)}
            renderInput={(params) => (
              <TextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...params}
                label="19 - CBO (especialidade)"
                name="professional_solicitant_cbo"
                data-ga="professional_solicitant_cbo"
                error={
                  !!(
                    errors.professional_solicitant_cbo &&
                    touched.professional_solicitant_cbo
                  )
                }
                helperText={
                  touched.professional_solicitant_cbo &&
                  errors.professional_solicitant_cbo
                }
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6} lg={3}>
          <SelectFieldSADT
            label="21 - Caráter do atendimento"
            identifier="solicitor_service_type"
          >
            {serviceCharacters.map((serviceCharacter) => (
              <MenuItem value={serviceCharacter.id} key={serviceCharacter.id}>
                {serviceCharacter.term}
              </MenuItem>
            ))}
          </SelectFieldSADT>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3}>
          <DatePickerSADT
            label="22 - Data da solicitação"
            identifier="request_date"
          />
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={6}>
          <TextFieldSADT
            label="23 - Indicação clínica"
            identifier="clinical_indication"
            maxLength={150}
          />
        </Grid>
      </Grid>
    </CollapsibleFormSection>
  );
};

export default forwardRef(SolicitorSection);
